import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Users2, Lock } from "lucide-react";
import { fmtDate } from "@/lib/format";
import EmptyState from "@/components/EmptyState";

const CATS = ["general","props","costume","lighting","sound","staging","blocking","music"];
const PRIOS = ["low","normal","high"];

const CAT_COLORS = {
  general: "border-zinc-500/30 text-zinc-300",
  props: "border-amber-500/30 text-amber-300",
  costume: "border-pink-500/30 text-pink-300",
  lighting: "border-emerald-500/30 text-emerald-300",
  sound: "border-blue-500/30 text-blue-300",
  staging: "border-purple-500/30 text-purple-300",
  blocking: "border-red-500/30 text-red-300",
  music: "border-teal-500/30 text-teal-300",
};

export default function Notes({ embedded = false }) {
  const { user } = useAuth();
  const canEdit = user.role === "org_admin" || user.role === "production_staff";
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ title: "", category: "general", content: "", priority: "normal", tagged_user_ids: [] });

  const load = () => api.get("/notes").then((r) => setNotes(r.data));
  useEffect(() => {
    load();
    if (canEdit) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [canEdit]);

  const castUsers = useMemo(() => users.filter((u) => u.role === "cast"), [users]);
  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const toggleTag = (uid) => {
    setForm((f) => {
      const has = f.tagged_user_ids.includes(uid);
      return { ...f, tagged_user_ids: has ? f.tagged_user_ids.filter((x) => x !== uid) : [...f.tagged_user_ids, uid] };
    });
  };
  const tagAllCast = () => setForm((f) => ({ ...f, tagged_user_ids: castUsers.map((c) => c.id) }));
  const clearTags = () => setForm((f) => ({ ...f, tagged_user_ids: [] }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    await api.post("/notes", form);
    toast.success(form.tagged_user_ids.length ? `Note posted · visible to ${form.tagged_user_ids.length} cast member${form.tagged_user_ids.length === 1 ? "" : "s"}` : "Note posted · staff only");
    setOpen(false);
    setForm({ title: "", category: "general", content: "", priority: "normal", tagged_user_ids: [] });
    load();
  };
  const del = async (id) => {
    await api.delete(`/notes/${id}`);
    load();
  };

  const shown = filter === "all" ? notes : notes.filter((n) => n.category === filter);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="notes-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Production desk</div>
          <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Production Notes</h1>
          <p className="mt-1 text-zinc-400 text-sm">Rehearsal notes, cues, fixes and reminders.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="bg-[#121212] border-white/10 w-40"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white">
              <SelectItem value="all">All categories</SelectItem>
              {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {canEdit && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-note-btn" className="bg-gold text-black hover:bg-gold-hover"><Plus className="w-4 h-4 mr-1" />Add note</Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121212] border-white/10 text-white">
                <DialogHeader><DialogTitle className="font-display">New production note</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Title</Label><Input data-testid="note-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Category</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10 text-white">
                          {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Priority</Label>
                      <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                        <SelectTrigger className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10 text-white">
                          {PRIOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Content</Label><Textarea data-testid="note-content-input" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
                  <div className="pt-1">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-[11px] uppercase tracking-[0.24em] text-zinc-500 inline-flex items-center gap-1.5"><Users2 className="w-3.5 h-3.5" /> Tag cast members</Label>
                      <div className="flex items-center gap-1">
                        <button type="button" data-testid="tag-all-cast" onClick={tagAllCast} className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white h-7 px-2 rounded border border-white/10">All cast</button>
                        <button type="button" data-testid="clear-tagged" onClick={clearTags} className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white h-7 px-2 rounded border border-white/10">Clear</button>
                      </div>
                    </div>
                    <div className="rounded-md border border-white/10 bg-[#0A0A0A] p-2 max-h-40 overflow-y-auto flex flex-wrap gap-1.5">
                      {castUsers.length === 0 && <div className="text-[11px] text-zinc-500 px-1">No cast members yet.</div>}
                      {castUsers.map((u) => {
                        const on = form.tagged_user_ids.includes(u.id);
                        return (
                          <button key={u.id} type="button" data-testid={`tag-user-${u.id}`} onClick={() => toggleTag(u.id)}
                            className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${on ? "bg-gold/15 text-gold border-gold/40" : "border-white/10 text-zinc-300 hover:bg-white/5"}`}>
                            {u.name}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-2 text-[11px] text-zinc-500 inline-flex items-center gap-1.5">
                      <Lock className="w-3 h-3" />
                      {form.tagged_user_ids.length === 0
                        ? "Staff & admin only — cast will not see this note."
                        : `Visible to ${form.tagged_user_ids.length} cast member${form.tagged_user_ids.length === 1 ? "" : "s"} plus staff & admin.`}
                    </div>
                  </div>
                </div>
                <DialogFooter><Button data-testid="save-note-btn" onClick={save} className="bg-gold text-black hover:bg-gold-hover">Save note</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.length === 0 && (
          <div className="col-span-full">
            <EmptyState
              icon={Users2}
              title={user.role === "cast" ? "No notes for you yet" : "No production notes yet"}
              body={user.role === "cast"
                ? "You'll see notes here when staff or the director tags you in one."
                : "Post a note for the team. Tag specific cast members so they receive it too — otherwise it stays staff-only."}
            />
          </div>
        )}
        {shown.map((n) => (
          <div key={n.id} className="rounded-lg border border-white/10 bg-[#121212] p-5 hover:border-white/20 transition-colors" data-testid={`note-card-${n.id}`}>
            <div className="flex items-center justify-between">
              <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${CAT_COLORS[n.category] || CAT_COLORS.general}`}>{n.category}</span>
              <span className={`text-[10px] uppercase tracking-widest ${n.priority === "high" ? "text-red-400" : n.priority === "low" ? "text-zinc-500" : "text-zinc-400"}`}>{n.priority}</span>
            </div>
            <div className="mt-3 font-display text-lg leading-tight">{n.title}</div>
            <div className="mt-2 text-sm text-zinc-400 whitespace-pre-wrap">{n.content}</div>
            {canEdit && (n.tagged_user_ids?.length > 0 ? (
              <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap items-center gap-1">
                <Users2 className="w-3 h-3 text-zinc-500 mr-1" />
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Visible to</span>
                {n.tagged_user_ids.slice(0, 6).map((uid) => (
                  <span key={uid} className="text-[10px] px-1.5 py-0.5 rounded-full bg-gold/10 text-gold border border-gold/30">{userById[uid]?.name || "cast"}</span>
                ))}
                {n.tagged_user_ids.length > 6 && <span className="text-[10px] text-zinc-500">+{n.tagged_user_ids.length - 6}</span>}
              </div>
            ) : (
              <div className="mt-3 pt-3 border-t border-white/5 text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Staff only</div>
            ))}
            <div className="mt-4 flex items-center justify-between text-[11px] text-zinc-500">
              <span>{n.created_by_name} · {fmtDate(n.created_at)}</span>
              {canEdit && <button data-testid={`del-note-${n.id}`} onClick={() => del(n.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
